import { useState } from "react";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-20 sm:py-28 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <Badge className="bg-green-100 text-green-800 border-0 mb-4 px-4 py-1.5 rounded-full text-sm font-medium">
            Get In Touch
          </Badge>
          <h2 className="font-[Outfit] text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight mb-4">
            Contact Us
          </h2>
          <p className="text-base md:text-lg text-stone-500 max-w-2xl mx-auto">
            Have questions or need to reach us? We're here to help.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Contact Info */}
          <div>
            <div className="space-y-6">
              <div className="flex items-start gap-4" data-testid="contact-address">
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-green-700" />
                </div>
                <div>
                  <h4 className="font-[Outfit] font-semibold text-stone-900 mb-1">Our Location</h4>
                  <p className="text-sm text-stone-600 leading-relaxed">
                    No Prof 3 Olu Longe Street,<br />
                    Off Lekan Salami Estate,<br />
                    Bodija, Ibadan
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-phones">
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-green-700" />
                </div>
                <div>
                  <h4 className="font-[Outfit] font-semibold text-stone-900 mb-1">Phone Numbers</h4>
                  <a href="tel:09162731000" className="text-sm text-stone-600 hover:text-green-700 transition-colors block">
                    09162731000
                  </a>
                  <a href="tel:09162741000" className="text-sm text-stone-600 hover:text-green-700 transition-colors block">
                    09162741000
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-email">
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-green-700" />
                </div>
                <div>
                  <h4 className="font-[Outfit] font-semibold text-stone-900 mb-1">Email</h4>
                  <a href="mailto:info@ivmedicsclinic.com" className="text-sm text-stone-600 hover:text-green-700 transition-colors">
                    info@ivmedicsclinic.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-hours">
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-green-700" />
                </div>
                <div>
                  <h4 className="font-[Outfit] font-semibold text-stone-900 mb-1">Operating Hours</h4>
                  <p className="text-sm text-stone-600 font-medium">24 Hours, 7 Days a Week</p>
                  <p className="text-xs text-stone-400 mt-1">Always open for you</p>
                </div>
              </div>
            </div>

            {/* WhatsApp Button */}
            <a
              href="https://wa.me/2349162731000?text=Hello%20IV%20Medics,%20I%20would%20like%20to%20make%20an%20enquiry."
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 flex items-center justify-center gap-3 bg-green-600 hover:bg-green-700 text-white rounded-full px-6 py-4 transition-all shadow-md hover:shadow-lg w-full sm:w-auto"
              data-testid="contact-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              <span className="font-medium">Chat with us on WhatsApp</span>
            </a>
          </div>

          {/* Contact Form */}
          <div className="bg-stone-50 rounded-3xl p-6 sm:p-8 border border-stone-100">
            <h3 className="font-[Outfit] text-xl font-semibold text-stone-900 mb-6">
              Send us a Message
            </h3>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div>
                <Label className="text-stone-700 text-sm font-medium mb-2 block">Full Name *</Label>
                <Input
                  data-testid="contact-name-input"
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-white rounded-xl border-stone-200 h-12"
                />
              </div>
              <div>
                <Label className="text-stone-700 text-sm font-medium mb-2 block">Email Address *</Label>
                <Input
                  data-testid="contact-email-input"
                  type="email"
                  placeholder="your@email.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-white rounded-xl border-stone-200 h-12"
                />
              </div>
              <div>
                <Label className="text-stone-700 text-sm font-medium mb-2 block">Phone (Optional)</Label>
                <Input
                  data-testid="contact-phone-input"
                  placeholder="08012345678"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-white rounded-xl border-stone-200 h-12"
                />
              </div>
              <div>
                <Label className="text-stone-700 text-sm font-medium mb-2 block">Message *</Label>
                <Textarea
                  data-testid="contact-message-input"
                  placeholder="How can we help you?"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="bg-white rounded-xl border-stone-200 min-h-[120px]"
                />
              </div>
              <Button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={sending}
                className="w-full bg-green-700 hover:bg-green-800 text-white rounded-full py-6 gap-2 text-base"
              >
                {sending ? "Sending..." : "Send Message"} <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
